import React, { useState, useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import CommentItem from './CommentItem';
import { toast } from 'react-toastify';

export default function CommentSection({ contentId, contentType }) {
    const auth = getAuth();
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [replyingTo, setReplyingTo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const commentInputRef = useRef(null);
    const commentsEndRef = useRef(null);

    // Determine the collection path based on content type
    const getCollectionPath = () => {
        switch (contentType) {
            case 'listing':
                return `listings/${contentId}/comments`;
            case 'status':
                return `status/${contentId}/comments`;
            default:
                throw new Error('Invalid content type for comments');
        }
    };

    // Fetch comments in real-time
    useEffect(() => {
        if (!contentId) return;

        const collectionPath = getCollectionPath();
        const commentsRef = collection(db, collectionPath);
        const q = query(commentsRef, orderBy('timestamp', 'asc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const commentsData = [];
            snapshot.forEach((doc) => {
                commentsData.push({ id: doc.id, ...doc.data() });
            });
            setComments(commentsData);
            setIsLoading(false);
            
            // Scroll to bottom when new comments arrive
            setTimeout(() => {
                commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }, (error) => {
            console.error("Error fetching comments:", error);
            toast.error("Failed to load comments");
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [contentId, contentType]);

    // Handle comment submission
    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        if (!newComment.trim() || !auth.currentUser) return;

        try {
            const collectionPath = getCollectionPath();
            const commentsRef = collection(db, collectionPath);
            
            const commentData = {
                userId: auth.currentUser.uid,
                userPhoto: auth.currentUser.photoURL || null,
                userName: auth.currentUser.displayName || 'Anonymous',
                text: newComment.trim(),
                timestamp: serverTimestamp(),
                likes: 0,
                likedBy: [],
                replyCount: 0,
                replies: [],
                isReply: false,
                parentCommentId: replyingTo?.commentId || null
            };

            // Add the comment
            const docRef = await addDoc(commentsRef, commentData);
            
            // If this is a reply, update the parent comment
            if (replyingTo) {
                const parentCommentRef = doc(db, collectionPath, replyingTo.commentId);
                await updateDoc(parentCommentRef, {
                    replyCount: replyingTo.replyCount + 1,
                    replies: arrayUnion(docRef.id)
                });
            }

            // Update the content document's comment count
            const contentRef = doc(db, contentType === 'listing' ? 'listings' : 'status', contentId);
            await updateDoc(contentRef, {
                commentCount: comments.length + 1
            });

            setNewComment('');
            setReplyingTo(null);
            commentInputRef.current?.focus();
        } catch (error) {
            console.error("Error adding comment:", error);
            toast.error("Failed to post comment");
        }
    };

    // Handle reply action
    const handleReply = (comment) => {
        setReplyingTo({
            commentId: comment.id,
            userName: comment.userName,
            replyCount: comment.replyCount
        });
        commentInputRef.current?.focus();
    };

    // Cancel reply
    const handleCancelReply = () => {
        setReplyingTo(null);
    };

    return (
        <div className="flex flex-col h-full bg-gray-900 text-white">
            {/* Comment Header */}
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
                <h3 className="text-lg font-bold">Comments ({comments.length})</h3>
                <button className="text-gray-400 hover:text-white">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {/* Comments List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {isLoading ? (
                    <div className="flex justify-center items-center h-full">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-pink-500"></div>
                    </div>
                ) : comments.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                        No comments yet. Be the first to comment!
                    </div>
                ) : (
                    comments.map((comment) => (
                        <CommentItem 
                            key={comment.id} 
                            comment={comment} 
                            contentId={contentId} 
                            contentType={contentType} 
                            onReply={handleReply}
                        />
                    ))
                )}
                <div ref={commentsEndRef} />
            </div>

            {/* Comment Input */}
            <div className="p-4 border-t border-gray-700">
                {replyingTo && (
                    <div className="flex items-center justify-between mb-2 px-2 py-1 bg-gray-800 rounded">
                        <span className="text-sm text-pink-400">Replying to @{replyingTo.userName}</span>
                        <button 
                            onClick={handleCancelReply}
                            className="text-gray-400 hover:text-white text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                )}
                <form onSubmit={handleCommentSubmit} className="flex items-center">
                    <input
                        ref={commentInputRef}
                        type="text"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment..."
                        className="flex-1 bg-gray-800 rounded-full px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
                    />
                    <button
                        type="submit"
                        disabled={!newComment.trim()}
                        className={`ml-2 px-4 py-2 rounded-full ${newComment.trim() ? 'bg-pink-500 text-white' : 'bg-gray-700 text-gray-500'} font-medium`}
                    >
                        Post
                    </button>
                </form>
            </div>
        </div>
    );
}